import React, { useEffect, useState, useMemo } from "react";
import { db } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { useBuilding } from "../../context/BuildingContext";
import { useToast } from "../../components/Toast";
import { formatCurrencyUY } from "../../utils/formatters";
import { tolerantId } from "../../utils/firestoreHelpers";
import { calculateFundBalances, calculateUnitSummary, calculateUnitSummaryTotals } from "../../utils/fundBalances";
import { resolveCierreWindow } from "../../utils/cierrePeriod";
import { gastosDelInforme } from "../../utils/gastosDelMes";
import { detalleCorto } from "../../utils/detalleGasto";
import { colors, typography, spacing, shadows, radius, cardBase, btnPrimary, transitions } from "../../utils/designTokens";
import {
  FaFileAlt,
  FaBuilding,
  FaCalendarAlt,
  FaPrint,
  FaWallet,
  FaUsers,
  FaReceipt,
} from "react-icons/fa";

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const EXPENSE_GROUPS = {
  obras: "OBRAS Y PROYECTOS",
  incidencias: "INCIDENCIAS",
  consumos: "CONSUMOS",
  servicios: "SERVICIOS",
  mantenimiento: "MANTENIMIENTO",
  administracion: "ADMINISTRACIÓN",
  seguros: "SEGUROS",
  impuestos: "IMPUESTOS",
  otros: "OTROS",
};

const sinAcentos = (t) => String(t || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// Rubro del gasto para agrupar el detalle, como en una liquidación (consumos,
// servicios, impuestos…). Mira el proveedor y la descripción.
function guessGroup(expense, supplierName = "") {
  if (expense.expenseCategory === "obra") return "obras";
  if (expense.expenseCategory === "incidencia") return "incidencias";
  const prov = sinAcentos(supplierName);
  if (/^(ute|ose|antel|montevideo gas)\b/.test(prov)) return "consumos";
  const desc = sinAcentos(`${expense.shortDescription || ""} ${expense.description || ""}`);
  if (desc.includes("ute") || desc.includes("ose") || desc.includes("antel") || desc.includes("gas") || desc.includes("agua") || desc.includes("electric")) return "consumos";
  if (desc.includes("limpieza") || desc.includes("portero") || desc.includes("vigilancia") || desc.includes("jardin")) return "servicios";
  if (desc.includes("mantenim") || desc.includes("reparac") || desc.includes("ascensor") || desc.includes("electri") || desc.includes("plomer")) return "mantenimiento";
  if (desc.includes("admin") || desc.includes("honor") || desc.includes("contad")) return "administracion";
  if (desc.includes("seguro") || desc.includes("poliza") || desc.includes("bse")) return "seguros";
  if (desc.includes("impuesto") || desc.includes("contrib") || desc.includes("bps") || desc.includes("dgi")) return "impuestos";
  return "otros";
}

const InformeGastosComunes = ({ setSelected }) => {
  const { showToast } = useToast();
  const { selectedBuilding } = useBuilding();
  const buildingId = selectedBuilding?.buildingid;

  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [multas, setMultas] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [buildingData, setBuildingData] = useState({});
  const [suppliersMap, setSuppliersMap] = useState({});
  const [cierres, setCierres] = useState([]);
  const [filasAplicadas, setFilasAplicadas] = useState([]);

  useEffect(() => {
    if (!buildingId) return;
    fetchAll();
  }, [buildingId, month, year]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [expSnap, paySnap, multSnap, incSnap, aptSnap, bldSnap, supSnap, cierreSnap, filasSnap] = await Promise.all([
        getDocs(query(collection(db, "expenses"), where("buildingid", "in", tolerantId(buildingId)))),
        getDocs(query(collection(db, "payments"), where("buildingid", "in", tolerantId(buildingId)))),
        getDocs(query(collection(db, "multas"), where("buildingid", "in", tolerantId(buildingId)))),
        getDocs(query(collection(db, "buildingincomes"), where("buildingid", "in", tolerantId(buildingId)))),
        getDocs(query(collection(db, "apartments"), where("building", "in", tolerantId(buildingId)))),
        getDocs(query(collection(db, "building"), where("buildingid", "in", tolerantId(buildingId)))),
        getDocs(collection(db, "supplier")),
        // Cierres y filas de reparto: qué gastos entran en los gastos comunes del mes.
        getDocs(query(collection(db, "montlyaccount"), where("buildingid", "in", tolerantId(buildingId)))),
        getDocs(query(collection(db, "expensesapartments"), where("buildingid", "in", tolerantId(buildingId)))),
      ]);
      setCierres(cierreSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setFilasAplicadas(filasSnap.docs.map((d) => d.data() || {}));

      setExpenses(expSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setPayments(paySnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setMultas(multSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setIncomes(incSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setApartments(aptSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setBuildingData(bldSnap.docs[0]?.data() || {});

      const sMap = {};
      supSnap.docs.forEach((d) => {
        const data = d.data();
        if (data.supplierId) sMap[String(data.supplierId)] = data.name || data.nombre || "";
        sMap[d.id] = data.name || data.nombre || "";
      });
      setSuppliersMap(sMap);
    } catch (err) {
      console.error("Error:", err);
      showToast("Error al cargar datos", "error");
    } finally {
      setLoading(false);
    }
  };

  // Gastos de los gastos comunes del mes: se reparten por FECHA DE PAGO (lo
  // pagado en octubre va en los gastos comunes de octubre, que se pagan en
  // noviembre). Con el cierre hecho, su lista exacta; si no, la misma
  // selección que haría el cierre (vista preliminar).
  const informe = useMemo(
    () => gastosDelInforme({ expenses, cierres, filasAplicadas, buildingId, year, month, diaCorte: buildingData?.diaCorte ?? null }),
    [expenses, cierres, filasAplicadas, buildingId, year, month, buildingData]
  );
  const monthExpenses = informe.gastos;

  // Agrupar gastos por rubro
  const expensesByGroup = useMemo(() => {
    const groups = {};
    monthExpenses.forEach((e) => {
      const g = guessGroup(e, suppliersMap[String(e.supplierid)]);
      if (!groups[g]) groups[g] = [];
      groups[g].push(e);
    });
    return groups;
  }, [monthExpenses, suppliersMap]);

  const totalExpenses = monthExpenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);

  // Ventana del período (fecha de corte configurable). Con día de corte, los gastos
  // se bucketean por la ventana (por fecha de pago), no por mes calendario del
  // invoiceDate → un gasto pagado el 04/10 entra en el informe de septiembre.
  const diaCorte = buildingData?.diaCorte ?? null;
  const periodWindow = useMemo(() => resolveCierreWindow(month, year, { diaCorte }), [month, year, diaCorte]);
  const pStart = diaCorte ? periodWindow.start : null;
  const pEnd = diaCorte ? periodWindow.end : null;

  // Saldos por fondo
  const fundsUYU = useMemo(() =>
    calculateFundBalances({ expenses, payments, multas, incomes, building: buildingData, year, month, currency: "peso", periodStart: pStart, periodEnd: pEnd }),
    [expenses, payments, multas, incomes, buildingData, year, month, pStart, pEnd]
  );
  const fundsUSD = useMemo(() =>
    calculateFundBalances({ expenses, payments, multas, incomes, building: buildingData, year, month, currency: "dolar", periodStart: pStart, periodEnd: pEnd }),
    [expenses, payments, multas, incomes, buildingData, year, month, pStart, pEnd]
  );
  const hasUSD = fundsUSD.total.cobranza > 0 || fundsUSD.total.gastos > 0 || fundsUSD.total.saldoAnterior > 0 || fundsUSD.total.creditos > 0;

  // Resumen por unidad: cuenta corriente (el saldo anterior es el total a pagar
  // del mes anterior y los pagos van en el mes en que se cobraron).
  const unitRows = useMemo(
    () => calculateUnitSummary({ apartments, payments, year, month, periodStart: pStart, periodEnd: pEnd }),
    [apartments, payments, year, month, pStart, pEnd]
  );
  const unitTotals = useMemo(() => calculateUnitSummaryTotals(unitRows), [unitRows]);
  const sinCargosDelMes = unitTotals.totalMes === 0 && unitTotals.recargoMora === 0;

  const fmt = (v) => formatCurrencyUY(v);
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;

  // Columnas dinámicas por fondo personalizado para la tabla "Gastos a pagar por"
  const activeCustomFunds = useMemo(
    () => (buildingData?.customFunds || []).filter(f => f.active !== false),
    [buildingData]
  );

  if (!buildingId) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <FaBuilding size={48} color={colors.gray400} />
          <p style={{ color: colors.gray600, marginTop: 16 }}>Seleccioná un edificio</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Encabezado */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>
            <FaFileAlt style={{ marginRight: 8 }} /> Informe Gastos Comunes
          </h2>
          <p style={styles.subtitle}>{selectedBuilding?.name || "Edificio"} — {MONTHS[month - 1]} {year}</p>
        </div>
        <button style={styles.btnPrimary} onClick={() => window.print()}>
          <FaPrint size={14} /> Imprimir
        </button>
      </div>

      {/* Selector de período */}
      <div style={styles.filterRow}>
        <div style={styles.periodRow}>
          <FaCalendarAlt size={14} color={colors.gray600} />
          <select style={styles.periodSelect} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <input type="number" style={{ ...styles.periodSelect, width: 80 }} value={year} onChange={(e) => setYear(Number(e.target.value))} min={2020} max={2100} />
        </div>
      </div>

      {loading ? (
        <div style={styles.emptyState}><div style={styles.spinner} /></div>
      ) : (
        <>
          {/* Tarjetas KPI */}
          <div style={styles.kpiRow}>
            <div style={styles.kpiCard}>
              <div style={styles.kpiValue}>{fmt(totalExpenses)}</div>
              <div style={styles.kpiLabel}>Total gastos del mes</div>
            </div>
            <div style={{ ...styles.kpiCard, borderLeftColor: colors.success }}>
              <div style={{ ...styles.kpiValue, color: colors.success }}>{fmt(fundsUYU.total.cobranza)}</div>
              <div style={styles.kpiLabel}>Cobranza</div>
            </div>
            <div style={{ ...styles.kpiCard, borderLeftColor: colors.error }}>
              <div style={{ ...styles.kpiValue, color: colors.error }}>{fmt(fundsUYU.total.gastos)}</div>
              <div style={styles.kpiLabel}>Egresos</div>
            </div>
            <div style={{ ...styles.kpiCard, borderLeftColor: colors.warning }}>
              <div style={{ ...styles.kpiValue, color: colors.warning }}>{Object.keys(expensesByGroup).length}</div>
              <div style={styles.kpiLabel}>Categorías</div>
            </div>
          </div>

          {/* Sección 1: Detalle de gastos (los que entran en los gastos comunes del mes) */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>
              <FaReceipt size={14} /> Detalle de Gastos del Mes
            </h3>
            <p style={styles.nota}>
              {informe.fuente === "cierre"
                ? `Gastos liquidados en el cierre de ${MONTHS[month - 1].toLowerCase()}.`
                : `Vista preliminar: el cierre de ${MONTHS[month - 1].toLowerCase()} todavía no se hizo. Entran los gastos pagados hasta el fin del período que no se liquidaron antes.`}
            </p>
            <div style={styles.tableCard}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeadRow}>
                    <th style={styles.th}>Gasto</th>
                    <th style={styles.th}>Detalle</th>
                    <th style={styles.th}>Fecha de pago</th>
                    <th style={styles.thRight}>Importe $</th>
                  </tr>
                </thead>
                <tbody>
                  {monthExpenses.length === 0 && (
                    <tr style={styles.trEven}>
                      <td colSpan={4} style={{ ...styles.td, color: colors.gray600 }}>No hay gastos pagados para este período.</td>
                    </tr>
                  )}
                  {Object.entries(expensesByGroup).map(([group, items]) => (
                    <React.Fragment key={group}>
                      <tr style={styles.groupRow}>
                        <td colSpan={4} style={styles.groupLabel}>{EXPENSE_GROUPS[group] || group.toUpperCase()}</td>
                      </tr>
                      {items.map((e) => (
                        <tr key={e.id} style={styles.trEven}>
                          <td style={styles.td}>{suppliersMap[String(e.supplierid)] || "-"}</td>
                          <td style={styles.td} title={e.description || ""}>{detalleCorto(e) || "-"}</td>
                          <td style={{ ...styles.td, whiteSpace: "nowrap" }}>{fechaUY(e.paidDate)}</td>
                          <td style={styles.tdRight}>{fmt(e.amount)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={styles.tfootRow}>
                    <td colSpan={3} style={{ ...styles.td, fontWeight: 400 }}>TOTAL GASTOS</td>
                    <td style={{ ...styles.tdRight, fontWeight: 400 }}>{fmt(totalExpenses)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Sección 2: Resumen por fondo UYU */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>
              <FaWallet size={14} /> Resumen por Fondo — Pesos (UYU)
            </h3>
            <div style={styles.tableCard}>
              {renderFundTable(fundsUYU, fmt)}
            </div>
          </div>

          {/* Sección 3: Resumen por fondo USD */}
          {hasUSD && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>
                <FaWallet size={14} /> Resumen por Fondo — Dólares (USD)
              </h3>
              <div style={styles.tableCard}>
                {renderFundTable(fundsUSD, (v) => `US$ ${(v || 0).toLocaleString("es-UY")}`)}
              </div>
            </div>
          )}

          {/* Sección 4: Resumen por unidad */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>
              <FaUsers size={14} /> Gastos a pagar en {MONTHS[nextMonth - 1]} {nextYear}
            </h3>
            <p style={styles.nota}>
              El saldo anterior es el total a pagar de {MONTHS[(month + 10) % 12].toLowerCase()}; los pagos son los cobrados en {MONTHS[month - 1].toLowerCase()}.
              {informe.fuente !== "cierre" && sinCargosDelMes &&
                ` Los gastos comunes de ${MONTHS[month - 1].toLowerCase()} por unidad aparecen cuando se hace el cierre de ${MONTHS[month - 1].toLowerCase()}.`}
            </p>
            <div style={styles.tableCard}>
              <div style={{ overflowX: "auto" }}>
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.tableHeadRow}>
                      <th style={styles.th}>Nro</th>
                      <th style={styles.thRight}>Saldo Ant.</th>
                      <th style={styles.thRight}>Pagos</th>
                      <th style={styles.thRight}>Nuevo Saldo</th>
                      <th style={styles.thRight}>Gastos Com.</th>
                      <th style={styles.thRight}>Fdo. Reserva</th>
                      {/* Una columna por fondo personalizado activo (FIN-019) */}
                      {activeCustomFunds.map(f => (
                        <th key={f.id} style={styles.thRight}>{f.name || f.id}</th>
                      ))}
                      <th style={styles.thRight}>Total Mes</th>
                      <th style={styles.thRight}>Recargo Mora</th>
                      <th style={{ ...styles.thRight, fontWeight: 400 }}>Total a Pagar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unitRows.map((r, i) => (
                      <tr key={i} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                        <td style={{ ...styles.td, fontWeight: 400 }}>{r.label}</td>
                        <td style={styles.tdRight}>{fmt(r.saldoAnterior)}</td>
                        <td style={styles.tdRight}>{fmt(r.pagos)}</td>
                        <td style={styles.tdRight}>{fmt(r.nuevoSaldo)}</td>
                        <td style={styles.tdRight}>{fmt(r.gastosComunes)}</td>
                        <td style={styles.tdRight}>{fmt(r.fondoReserva)}</td>
                        {activeCustomFunds.map(f => (
                          <td key={f.id} style={styles.tdRight}>{fmt((r.customFunds && r.customFunds[f.id]) || 0)}</td>
                        ))}
                        <td style={styles.tdRight}>{fmt(r.totalMes)}</td>
                        <td style={{ ...styles.tdRight, color: r.recargoMora > 0 ? colors.error : undefined }}>{fmt(r.recargoMora)}</td>
                        <td style={{ ...styles.tdRight, fontWeight: 400 }}>{fmt(r.totalAPagar)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={styles.tfootRow}>
                      <td style={{ ...styles.td, fontWeight: 400 }}>TOTAL</td>
                      <td style={styles.tdRight}>{fmt(unitTotals.saldoAnterior)}</td>
                      <td style={styles.tdRight}>{fmt(unitTotals.pagos)}</td>
                      <td style={styles.tdRight}>{fmt(unitTotals.nuevoSaldo)}</td>
                      <td style={styles.tdRight}>{fmt(unitTotals.gastosComunes)}</td>
                      <td style={styles.tdRight}>{fmt(unitTotals.fondoReserva)}</td>
                      {activeCustomFunds.map(f => (
                        <td key={f.id} style={styles.tdRight}>{fmt((unitTotals.customFunds && unitTotals.customFunds[f.id]) || 0)}</td>
                      ))}
                      <td style={styles.tdRight}>{fmt(unitTotals.totalMes)}</td>
                      <td style={styles.tdRight}>{fmt(unitTotals.recargoMora)}</td>
                      <td style={{ ...styles.tdRight, fontWeight: 400 }}>{fmt(unitTotals.totalAPagar)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* Información de pie */}
          <div style={styles.infoBar}>
            <span style={{ fontWeight: 400 }}>
              Vencimiento gastos comunes día {buildingData.paymentDueDay || 10} de cada mes
            </span>
            <span style={{ color: colors.gray600 }}>
              Habitia — Plataforma de administración de edificios
            </span>
          </div>
        </>
      )}
    </div>
  );

  function renderFundTable(funds, formatFn) {
    // Filas dinámicas: built-ins (Caja, Reserva) + fondos personalizados activos del edificio.
    // Ya no hay fila "OBRAS" built-in: los gastos categoría "obra" se debitan de Reserva por defecto,
    // y quien quiera un bucket separado debe crearlo como fondo personalizado.
    const rows = [
      { label: "CAJA", data: funds.caja },
      { label: "RESERVA", data: funds.reserva },
    ];
    const activeCustomFunds = (buildingData?.customFunds || []).filter(f => f.active !== false);
    activeCustomFunds.forEach(fund => {
      if (funds[fund.id]) {
        rows.push({ label: (fund.name || fund.id).toUpperCase(), data: funds[fund.id] });
      }
    });
    return (
      <div style={{ overflowX: "auto" }}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeadRow}>
              <th style={styles.th}>Fondo</th>
              <th style={styles.thRight}>Saldo Anterior</th>
              <th style={styles.thRight}>Cobranza</th>
              <th style={styles.thRight}>Gastos</th>
              <th style={styles.thRight}>Créditos</th>
              <th style={{ ...styles.thRight, fontWeight: 400 }}>Saldo Actual</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                <td style={{ ...styles.td, fontWeight: 400 }}>{r.label}</td>
                <td style={styles.tdRight}>{formatFn(r.data.saldoAnterior)}</td>
                <td style={{ ...styles.tdRight, color: colors.success }}>{formatFn(r.data.cobranza)}</td>
                <td style={{ ...styles.tdRight, color: colors.error }}>{formatFn(r.data.gastos)}</td>
                <td style={{ ...styles.tdRight, color: colors.success }}>{formatFn(r.data.creditos)}</td>
                <td style={{ ...styles.tdRight, fontWeight: 400 }}>{formatFn(r.data.saldoActual)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={styles.tfootRow}>
              <td style={{ ...styles.td, fontWeight: 400 }}>TOTAL</td>
              <td style={styles.tdRight}>{formatFn(funds.total.saldoAnterior)}</td>
              <td style={{ ...styles.tdRight, color: colors.success }}>{formatFn(funds.total.cobranza)}</td>
              <td style={{ ...styles.tdRight, color: colors.error }}>{formatFn(funds.total.gastos)}</td>
              <td style={{ ...styles.tdRight, color: colors.success }}>{formatFn(funds.total.creditos)}</td>
              <td style={{ ...styles.tdRight, fontWeight: 400 }}>{formatFn(funds.total.saldoActual)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }
};

function fechaUY(val) {
  const d = parseDate(val);
  return d ? d.toLocaleDateString("es-UY", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
}

function parseDate(val) {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  if (typeof val.toDate === "function") return val.toDate();
  if (val.seconds) return new Date(val.seconds * 1000);
  if (typeof val === "string") {
    const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(val) ? `${val}T12:00:00` : val);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

const styles = {
  container: {
    padding: spacing[6],
    fontFamily: typography.fontFamily,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: spacing[5],
    flexWrap: "wrap",
    gap: spacing[3],
  },
  title: {
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.primary,
    margin: 0,
    display: "flex",
    alignItems: "center",
  },
  subtitle: {
    fontSize: typography.sizes.sm,
    color: colors.gray600,
    margin: "4px 0 0",
  },
  // Botón primario reutilizable
  btnPrimary: {
    ...btnPrimary,
    fontSize: typography.sizes.sm,
  },
  filterRow: {
    display: "flex",
    gap: spacing[3],
    marginBottom: spacing[5],
    flexWrap: "wrap",
    alignItems: "center",
  },
  // Fila de selector de período — sin borde, solo sombra
  periodRow: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    padding: "8px 14px",
    background: colors.white,
    borderRadius: radius.md,
    boxShadow: shadows.xs,
  },
  periodSelect: {
    padding: "6px 10px",
    border: `1px solid ${colors.gray300}`,
    borderRadius: radius.md,
    fontSize: typography.sizes.sm,
    outline: "none",
    background: colors.white,
  },
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: spacing[3],
    marginBottom: spacing[5],
  },
  kpiCard: {
    background: colors.white,
    borderRadius: radius.md,
    padding: "16px 20px",
    borderLeft: `4px solid ${colors.primary}`,
    boxShadow: shadows.sm,
  },
  kpiValue: {
    fontSize: 22,
    fontWeight: 400,
    color: colors.primary,
  },
  kpiLabel: {
    fontSize: typography.sizes.xs,
    color: colors.gray600,
    marginTop: 2,
  },
  section: {
    marginBottom: spacing[6],
  },
  nota: {
    fontSize: typography.sizes.xs,
    color: colors.gray600,
    margin: "0 0 10px",
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 400,
    color: colors.gray900,
    margin: "0 0 10px",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  // Tarjeta contenedora de tablas — sin borde, solo sombra
  tableCard: {
    ...cardBase,
    padding: 0,
    overflow: "hidden",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: typography.sizes.sm,
  },
  tableHeadRow: {
    background: colors.gray50,
  },
  th: {
    padding: "10px 14px",
    textAlign: "left",
    color: colors.gray600,
    fontSize: typography.sizes.xs,
    fontWeight: 400,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    whiteSpace: "nowrap",
  },
  thRight: {
    padding: "10px 14px",
    textAlign: "right",
    color: colors.gray600,
    fontSize: typography.sizes.xs,
    fontWeight: 400,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    whiteSpace: "nowrap",
  },
  trEven: { background: colors.white },
  trOdd: { background: colors.gray50 },
  td: {
    padding: "8px 14px",
    color: colors.gray900,
  },
  tdRight: {
    padding: "8px 14px",
    textAlign: "right",
    color: colors.gray900,
    whiteSpace: "nowrap",
  },
  tfootRow: {
    background: colors.gray100,
    borderTop: `2px solid ${colors.gray200}`,
  },
  groupRow: {
    background: colors.primarySubtle,
  },
  groupLabel: {
    padding: "6px 14px",
    fontWeight: 400,
    fontSize: typography.sizes.xs,
    color: colors.primary,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  // Barra de información al pie — sin borde, solo sombra
  infoBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    background: colors.white,
    borderRadius: radius.md,
    boxShadow: shadows.xs,
    fontSize: typography.sizes.sm,
    color: colors.primary,
    flexWrap: "wrap",
    gap: spacing[2],
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
  },
  spinner: {
    width: 32,
    height: 32,
    border: `3px solid ${colors.gray200}`,
    borderTopColor: colors.primary,
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
};

export default InformeGastosComunes;
